import { useCallback } from 'react'
import { CompaniesClient, useCompaniesClient } from '@frontend/clients/companies.request'
import { useClearCompanyAuditEventsCache } from '@frontend/queries/companies'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  BadgeCreationPayload,
  BadgeUpdatePayload,
  CompanyFormCreationPayload,
  CompanyFormUpdatePayload,
  DriverCreationPayload,
  DriverUpdatePayload,
} from '@shared-types/company'
import {
  CompanyLogoUpdatePayload,
  CompanyUpdatePayload,
  RampCreationPayload,
} from '@shared-types/domain'
import { CompanySettingsUpdatePayload, DBCompanySettingUpdatePayload } from '@shared-types/settings'

export function useClearCompaniesCache() {
  const queryClient = useQueryClient()

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['companies'] })
  }, [queryClient])
}

const useCompaniesClientRequest = () => {
  const companiesClient = useCompaniesClient()
  const clearCompanyCache = useClearCompaniesCache()
  const clearCompanyAuditEventsCache = useClearCompanyAuditEventsCache()

  return async <T>(func: (companiesClient: CompaniesClient) => Promise<T>): Promise<T> => {
    const result = await func(companiesClient)
    await clearCompanyCache()
    await clearCompanyAuditEventsCache()
    return result
  }
}

type UseMutateCreateDriver = {
  companyId: string
  payload: DriverCreationPayload
}
export const useMutateCreateDriver = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateCreateDriver) =>
      companiesClientRequest((client) => client.createDriver(companyId, payload)),
  })
}

type UseMutateUpdateDriver = {
  companyId: string
  driverId: string
  payload: DriverUpdatePayload
}
export const useMutateUpdateDriver = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, driverId, payload }: UseMutateUpdateDriver) =>
      companiesClientRequest((client) => client.updateDriver(companyId, driverId, payload)),
  })
}

type UseMutateDeleteDriver = {
  companyId: string
  driverId: string
}
export const useMutateDeleteDriver = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, driverId }: UseMutateDeleteDriver) =>
      companiesClientRequest((client) => client.deleteDriver(companyId, driverId)),
  })
}

type UseMutateUpdateCompany = {
  companyId: string
  payload: CompanyUpdatePayload
}
export const useMutateUpdateCompany = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateUpdateCompany) =>
      companiesClientRequest((client) => client.update(companyId, payload)),
  })
}

type UseMutateUpdateCompanyLogo = {
  companyId: string
  payload: CompanyLogoUpdatePayload
}
export const useMutateUpdateCompanyLogo = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateUpdateCompanyLogo) =>
      companiesClientRequest((client) => client.updateLogo(companyId, payload)),
  })
}

type UseMutateCreateCompanyForm = {
  companyId: string
  payload: CompanyFormCreationPayload
}
export const useMutateCreateCompanyForm = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateCreateCompanyForm) =>
      companiesClientRequest((client) => client.createForm(companyId, payload)),
  })
}

type UseMutateUpdateCompanyForm = {
  companyId: string
  formId: string
  payload: CompanyFormUpdatePayload
}
export const useMutateUpdateCompanyForm = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, formId, payload }: UseMutateUpdateCompanyForm) =>
      companiesClientRequest((client) => client.updateForm(companyId, formId, payload)),
  })
}

type UseMutateCreateBadge = {
  companyId: string
  payload: BadgeCreationPayload
}
export const useMutateCreateBadge = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateCreateBadge) =>
      companiesClientRequest((client) => client.createBadge(companyId, payload)),
  })
}

type UseMutateUpdateBadge = {
  companyId: string
  badgeId: string
  payload: BadgeUpdatePayload
}
export const useMutateUpdateBadge = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, badgeId, payload }: UseMutateUpdateBadge) =>
      companiesClientRequest((client) => client.updateBadge(companyId, badgeId, payload)),
  })
}

type UseMutateCreateRamp = {
  companyId: string
  facilityId: string
  payload: RampCreationPayload
}
export const useMutateCreateRamp = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, facilityId, payload }: UseMutateCreateRamp) =>
      companiesClientRequest((client) => client.createRamp(companyId, facilityId, payload)),
  })
}

type UseMutateInternalUpdateCompanySetting = {
  companyId: string
  settingName: string
  payload: DBCompanySettingUpdatePayload
}
export const useMutateInternalUpdateCompanySetting = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({
      companyId,
      settingName,
      payload,
    }: UseMutateInternalUpdateCompanySetting) =>
      companiesClientRequest((client) =>
        client.internalUpdateCompanySetting(companyId, settingName, payload)
      ),
  })
}

type UseMutateUpdateCompanySettings = {
  companyId: string
  payload: CompanySettingsUpdatePayload
}
export const useMutateUpdateCompanySettings = () => {
  const companiesClientRequest = useCompaniesClientRequest()

  return useMutation({
    mutationFn: async ({ companyId, payload }: UseMutateUpdateCompanySettings) =>
      companiesClientRequest((client) => client.updateCompanySettings(companyId, payload)),
  })
}
